import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { platformApi } from '@/api/platform';
import type { AsyncState } from '@/hooks';
import type { InstitutionGroup } from '@/types/platform';
import styles from './platform-pages.module.css';

/**
 * The settings tab — the group's editable identity, and nothing else.
 *
 * THE FIELD SET IS THE PATCH CONTRACT. Core accepts a partial body of name,
 * description and countryCode; anything else would be a 400 the day it
 * shipped. There is deliberately NO deactivate control here: isActive exists
 * on the row but no route toggles it, and a switch that silently did nothing —
 * or worse, a 400 that reads as a bug — is the failure this console exists to
 * avoid. When a route exists, this tab is where it goes.
 *
 * CHANGES ARE RECORDED BY CORE. The PATCH carries no reason field, so none is
 * asked for: this is an edit form, not a destructive action, and core keeps its
 * own audit stream for the change.
 */
export function GroupSettingsPanel({
  group,
  groupsState,
  onRetry,
}: {
  group: InstitutionGroup | null;
  groupsState: AsyncState<InstitutionGroup[]>;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [countryCode, setCountryCode] = useState(group?.countryCode ?? '');
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (groupsState.status === 'loading' || groupsState.status === 'error') {
    return (
      <Panel title={t('platform.groups.settings.title')} icon="settings">
        <AsyncPanel state={groupsState} onRetry={onRetry}>
          {() => null}
        </AsyncPanel>
      </Panel>
    );
  }

  if (!group) {
    return (
      <Panel title={t('platform.groups.settings.title')} icon="settings">
        <p className={styles.sectionEmpty} role="status">
          {t('platform.groups.settings.unknownGroup')}
        </p>
      </Panel>
    );
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!group || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.structure.updateGroup(group.id, {
        name: name.trim() || undefined,
        description: description.trim() || undefined,
        countryCode: countryCode.trim() || undefined,
      });
      setSaved(true);
      onRetry();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Panel title={t('platform.groups.settings.title')} icon="settings">
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.groups.fieldName')}</span>
          <input
            className={styles.input}
            value={name}
            required
            onChange={(event) => {
              setName(event.target.value);
              setSaved(false);
            }}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.groups.fieldDescription')}</span>
          <textarea
            className={styles.textarea}
            rows={3}
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
              setSaved(false);
            }}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.groups.fieldCountry')}</span>
          <input
            className={styles.input}
            value={countryCode}
            maxLength={2}
            onChange={(event) => {
              setCountryCode(event.target.value.toUpperCase());
              setSaved(false);
            }}
          />
          <span className={styles.fieldHint}>{t('platform.groups.fieldCountryHint')}</span>
        </label>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        {saved && (
          <p className={styles.rowDetail} role="status">
            <Icon name="checkmark" size={14} />
            {t('platform.groups.settings.saved')}
          </p>
        )}

        <div className="modal-actions">
          <button type="submit" className="modal-button" disabled={submitting || name.trim().length === 0}>
            {submitting ? t('platform.confirm.submitting') : t('platform.groups.settings.save')}
          </button>
        </div>
      </form>
    </Panel>
  );
}
